import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Facebook, Link2, Unlink, Loader2, CheckCircle2, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";

function PagePicker({ pages, onPick, busy }) {
  if (!pages.length) {
    return <p className="text-sm text-[#65676b] rounded-lg bg-[#fff7e6] px-4 py-3" data-testid="fb-no-pages">Connexion pas encore terminée, ou aucune page trouvée. Cliquez sur « Reconnecter Facebook » et cochez votre page lors de l'autorisation (vous devez en être administrateur).</p>;
  }
  return (
    <div className="space-y-2" data-testid="fb-page-picker">
      <p className="text-sm font-semibold">Choisissez la page sur laquelle publier :</p>
      {pages.map(p => (
        <button key={p.id} type="button" disabled={busy} onClick={() => onPick(p.id)} data-testid={`fb-page-option-${p.id}`}
          className="w-full flex items-center gap-3 rounded-lg border border-[#ccd0d5] px-3 py-2 text-left hover:border-[#1877f2] hover:bg-[#e7f3ff] transition-colors">
          {p.picture ? <img src={p.picture} alt="" className="w-8 h-8 rounded-full" /> : <Facebook className="w-8 h-8 text-[#1877f2]" />}
          <span className="font-semibold text-sm">{p.name}</span>
        </button>
      ))}
    </div>
  );
}

export const FacebookConnect = () => {
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.fbConnection().then(setConn).catch(e => toast.error(apiError(e))), []);
  useEffect(() => {
    load().then(() => {
      if (params.get("fb") === "connected") {
        toast.success("Compte Facebook autorisé");
        setParams({}, { replace: true });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.url) { window.location.href = res.url; return; }
      setConn(res);
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const connect = () => run(() => api.fbConnect(window.location.origin));
  const pick = (id) => run(() => api.fbChoosePage(id), "Page Facebook connectée");
  const disconnect = () => window.confirm("Déconnecter votre page Facebook ? Les vidéos ne seront plus publiées automatiquement.") &&
    run(() => api.fbDisconnect(), "Page Facebook déconnectée");
  const toggleAuto = (v) => run(() => api.fbAutoPublish(v), v ? "Publication automatique activée" : "Publication automatique désactivée");

  if (!conn) return <section className="card-surface p-6 text-sm text-[#65676b]" data-testid="fb-connect-card"><Loader2 className="w-4 h-4 animate-spin inline" /> Chargement…</section>;
  return (
    <section className="card-surface p-6 space-y-4" data-testid="fb-connect-card">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#1877f2] grid place-items-center"><Facebook className="w-5 h-5 text-white" fill="white" /></span>
        <div>
          <h2 className="font-display text-lg font-bold">Ma page Facebook</h2>
          <p className="text-sm text-[#65676b]">BuzzlyMe publie vos vidéos directement sur votre page.</p>
        </div>
      </div>

      {conn.connected && conn.page ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-[#e9f7ef] px-4 py-3" data-testid="fb-connected">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <span className="text-sm">Connectée à <b data-testid="fb-page-name">{conn.page.name}</b>{conn.admin_page && " (page admin)"}</span>
          {!conn.admin_page && (
            <button type="button" className="btn-ghost ml-auto !py-1.5" disabled={busy} onClick={disconnect} data-testid="fb-disconnect-btn">
              <Unlink className="w-4 h-4" /> Déconnecter
            </button>
          )}
        </div>
      ) : conn.pending ? (
        <PagePicker pages={conn.pages} onPick={pick} busy={busy} />
      ) : null}

      {!conn.admin_page && !(conn.connected && conn.page) && (
        <button type="button" className="btn-primary" disabled={busy} onClick={connect} data-testid="fb-connect-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          {conn.pending ? "Reconnecter Facebook" : "Connecter ma page Facebook"}
        </button>
      )}

      <label className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${conn.connected ? "border-[#ccd0d5]" : "border-dashed border-[#ccd0d5] opacity-60"}`}>
        <Zap className="w-5 h-5 text-[#1877f2]" />
        <span className="flex-1">
          <span className="block text-sm font-semibold">Publication automatique</span>
          <span className="block text-xs text-[#65676b]">Chaque vidéo terminée est publiée tout de suite sur votre page.</span>
        </span>
        <Switch checked={conn.auto_publish} disabled={!conn.connected || busy} onCheckedChange={toggleAuto} data-testid="fb-auto-publish-switch" />
      </label>
    </section>
  );
};
